import React, { forwardRef, useEffect, useImperativeHandle, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../config/constants';

// Cache global pour les blobs audio avec versioning - CONSERVÉ
const audioCache = new Map();
const loadingPromises = new Map();

// Génère une clé de cache basée sur les propriétés qui affectent l'audio - CONSERVÉ
const generateCacheKey = (projectId, segment) => {
    const cacheableProperties = {
        file: segment.segment_file,
        text: segment.text || segment.translated_text || '',
        emotion: segment.emotion || 'normal',
        speed: segment.speed || 'normal',
        language: segment.translated_language || 'original',
        voice_id: segment.voice_id,
        pitch: segment.pitch,
        last_modified: segment.last_modified || segment.updated_at,
        start: segment.start,
        end: segment.end,
        audio_gain: segment.audio_gain,
        _refresh: segment._forceRefresh || null
    };

    const propertiesString = JSON.stringify(cacheableProperties);

    // Fix pour gérer les caractères Unicode
    try {
        const encodedString = encodeURIComponent(propertiesString);
        const hash = btoa(encodedString).slice(0, 16);
        return `${projectId}_${segment.segment_file}_${hash}`;
    } catch (e) {
        // Fallback avec un hash simple
        let hash = 0;
        for (let i = 0; i < propertiesString.length; i++) {
            const char = propertiesString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return `${projectId}_${segment.segment_file}_${Math.abs(hash)}`;
    }
};

// Trouve une entrée de cache pour un segment - CONSERVÉ
const findCacheEntryForSegment = (projectId, segmentFile) => {
    const prefix = `${projectId}_${segmentFile}_`;
    for (const [key, value] of audioCache.entries()) {
        if (key.startsWith(prefix)) {
            return { key, value };
        }
    }
    return null;
};

const AudioPlayer = forwardRef(({ speakerId, transcript, isPlaying, currentTime, muted, projectId }, ref) => {
    // État et refs essentiels
    const audioRef = useRef(null);
    const [currentSegment, setCurrentSegment] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cacheProgress, setCacheProgress] = useState(0);
    const [isCacheReady, setIsCacheReady] = useState(false);

    // Refs pour éviter les conditions de course
    const isPlayingRef = useRef(isPlaying);
    const mutedRef = useRef(muted);
    const currentSegmentRef = useRef(null);
    const playPromiseRef = useRef(null);
    const isMountedRef = useRef(true);

    // Log de montage/démontage pour debug
    useEffect(() => {
        console.log(`[${speakerId}] AudioPlayer MOUNTED - Instance ID: ${Math.random().toString(36).substr(2, 9)}`);
        isMountedRef.current = true;

        return () => {
            console.log(`[${speakerId}] AudioPlayer UNMOUNTING`);
            isMountedRef.current = false;
        };
    }, [speakerId]);

    // Synchroniser les refs avec les props
    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        mutedRef.current = muted;
    }, [muted]);

    // Nettoie les anciennes versions d'un segment du cache - CONSERVÉ
    const cleanOldVersionsFromCache = useCallback((projectId, segmentFile, currentCacheKey) => {
        const prefix = `${projectId}_${segmentFile}_`;
        const keysToDelete = [];

        for (const [key, blobUrl] of audioCache.entries()) {
            if (key.startsWith(prefix) && key !== currentCacheKey) {
                URL.revokeObjectURL(blobUrl);
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => audioCache.delete(key));

        if (keysToDelete.length > 0) {
            console.log(`[${speakerId}] Cleaned ${keysToDelete.length} old versions of ${segmentFile}`);
        }
    }, [speakerId]);

    // Télécharge et met en cache un fichier audio - CONSERVÉ MAIS AMÉLIORÉ
    const downloadAndCacheAudio = useCallback(async (segment) => {
        const cacheKey = generateCacheKey(projectId, segment);

        if (audioCache.has(cacheKey)) {
            console.log(`[${speakerId}] Using cached audio: ${segment.segment_file}`);
            return audioCache.get(cacheKey);
        }

        if (loadingPromises.has(cacheKey)) {
            return loadingPromises.get(cacheKey);
        }

        const downloadPromise = (async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');

                console.log(`[${speakerId}] Downloading: ${segment.segment_file}`);

                const audioResponse = await axios.get(
                    `${API_CONFIG.BASE_URL}/get_file/${projectId}/${segment.segment_file}`,
                    {
                        responseType: 'blob',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'audio/wav, audio/mpeg, application/octet-stream'
                        },
                        params: {
                            binary: 'true',
                            _t: Date.now()
                        }
                    }
                );

                const audioBlob = audioResponse.data;
                const blobUrl = URL.createObjectURL(audioBlob);

                audioCache.set(cacheKey, blobUrl);
                cleanOldVersionsFromCache(projectId, segment.segment_file, cacheKey);

                console.log(`[${speakerId}] Cached: ${segment.segment_file}`);
                return blobUrl;

            } catch (error) {
                console.error(`[${speakerId}] Failed to cache: ${segment.segment_file}`, error);
                audioCache.delete(cacheKey);
                throw error;
            } finally {
                loadingPromises.delete(cacheKey);
            }
        })();

        loadingPromises.set(cacheKey, downloadPromise);
        return downloadPromise;
    }, [projectId, cleanOldVersionsFromCache, speakerId]);

    // Récupère l'URL blob depuis le cache - CONSERVÉ
    const getCachedAudioUrl = useCallback((segment) => {
        const cacheKey = generateCacheKey(projectId, segment);
        return audioCache.get(cacheKey);
    }, [projectId]);

    // Trouve le segment au temps donné
    const findSegmentAtTime = useCallback((time) => {
        if (!transcript) return null;

        const speakerType = speakerId.startsWith('BACKGROUND') ? 'BACKGROUND' :
            speakerId.startsWith('SILENT') ? 'SILENT' : 'SPEAKER';
        const fullSpeakerId = `${speakerType}_${speakerId.split('_')[1]}`;

        return transcript
            .filter(t => t.speaker === fullSpeakerId && t.segment_file)
            .find(t => time >= (t.start - 0.01) && time < (t.end + 0.01));
    }, [transcript, speakerId]);

    // Fonction pour jouer l'audio de manière sécurisée
    const safePlay = useCallback(async () => {
        if (!audioRef.current || !isMountedRef.current) return;

        if (!audioRef.current.src || audioRef.current.src === '') {
            console.log(`[${speakerId}] Cannot play: no source`);
            return;
        }

        try {
            // Attendre la promesse précédente si elle existe
            if (playPromiseRef.current) {
                await playPromiseRef.current;
            }

            // Vérifier qu'on doit toujours jouer
            if (!isPlayingRef.current || !audioRef.current) return;

            playPromiseRef.current = audioRef.current.play();
            await playPromiseRef.current;

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(`[${speakerId}] Error playing:`, error);
            }
        } finally {
            playPromiseRef.current = null;
        }
    }, [speakerId]);

    // Fonction pour mettre en pause l'audio
    const safePause = useCallback(async () => {
        if (!audioRef.current) return;

        try {
            if (playPromiseRef.current) {
                await playPromiseRef.current;
            }

            if (audioRef.current && !audioRef.current.paused) {
                audioRef.current.pause();
                console.log(`[${speakerId}] Paused audio`);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(`[${speakerId}] Error pausing:`, error);
            }
        }
    }, [speakerId]);

    // Fonction pour charger et jouer un segment
    const loadAndPlaySegment = useCallback(async (segment, time) => {

        if (!segment) {
            // Si pas de segment, nettoyer l'audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
            setCurrentSegment(null);
            currentSegmentRef.current = null;
            return;
        }

        if (!segment || !isMountedRef.current) return;

        setIsLoading(true);
        await safePause();

        try {
            // Vérifier que le segment est toujours valide
            const isSegmentValid = transcript.some(t => t.segment_file === segment.segment_file);
            if (!isSegmentValid) {
                console.warn(`[${speakerId}] Segment ${segment.segment_file} no longer exists`);
                return;
            }

            // Obtenir l'URL audio (depuis cache ou téléchargement)
            let audioUrl = getCachedAudioUrl(segment);
            if (!audioUrl) {
                audioUrl = await downloadAndCacheAudio(segment);
            }

            if (!audioRef.current || !isMountedRef.current) return;

            // Charger la nouvelle source
            audioRef.current.src = audioUrl;

            // Attendre que l'audio soit prêt
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Load timeout')), 10000); // 10s au lieu de 5s
                let retryCount = 0;
                const maxRetries = 2;

                const tryLoad = () => {
                    const onCanPlay = () => {
                        clearTimeout(timeout);
                        audioRef.current.removeEventListener('canplay', onCanPlay);
                        audioRef.current.removeEventListener('error', onError);
                        resolve();
                    };

                    const onError = () => {
                        if (retryCount < maxRetries) {
                            retryCount++;
                            console.log(`[${speakerId}] Retry loading ${retryCount}/${maxRetries}`);
                            audioRef.current.load(); // Réessayer
                        } else {
                            clearTimeout(timeout);
                            audioRef.current.removeEventListener('canplay', onCanPlay);
                            audioRef.current.removeEventListener('error', onError);
                            reject(new Error('Load error after retries'));
                        }
                    };

                    audioRef.current.addEventListener('canplay', onCanPlay);
                    audioRef.current.addEventListener('error', onError);
                    audioRef.current.load();
                };

                tryLoad();
            });

            // Positionner le temps
            const relativeTime = Math.max(0, time - segment.start);
            audioRef.current.currentTime = relativeTime;

            // Appliquer le volume ET le mute
            const gain = segment.audio_gain || 0.8;
            audioRef.current.volume = mutedRef.current ? 0 : gain;
            audioRef.current.muted = mutedRef.current;

            console.log(`[${speakerId}] Loaded segment: ${segment.segment_file}, muted: ${mutedRef.current}, volume: ${audioRef.current.volume}`);

            // Mettre à jour l'état du segment actuel
            currentSegmentRef.current = segment;
            setCurrentSegment(segment);

            // Jouer si nécessaire
            if (isPlayingRef.current) {
                await safePlay();
            }

        } catch (error) {
            console.error(`[${speakerId}] Error loading segment:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [transcript, getCachedAudioUrl, downloadAndCacheAudio, safePause, safePlay, speakerId]);

    // Précharger tous les segments
    const preloadAllAudios = useCallback(async () => {
        // ✅ NOUVEAU : Vérifier si un undo est en cours
        if (window._undoInProgress) {
            console.log(`[${speakerId}] Undo in progress, skipping preload`);
            return;
        }

        const speakerType = speakerId.startsWith('BACKGROUND') ? 'BACKGROUND' :
            speakerId.startsWith('SILENT') ? 'SILENT' : 'SPEAKER';
        const fullSpeakerId = `${speakerType}_${speakerId.split('_')[1]}`;

        const speakerSegments = transcript.filter(t =>
            t.speaker === fullSpeakerId && t.segment_file
        );

        console.log(`[${speakerId}] Starting preload of ${speakerSegments.length} segments`);

        let loadedCount = 0;
        let skippedCount = 0;
        const batchSize = 3;

        for (let i = 0; i < speakerSegments.length; i += batchSize) {
            // ✅ NOUVEAU : Vérifier à chaque batch si undo en cours
            if (window._undoInProgress) {
                console.log(`[${speakerId}] Undo started during preload, stopping`);
                return;
            }

            const batch = speakerSegments.slice(i, i + batchSize);

            await Promise.allSettled(
                batch.map(async (segment) => {
                    try {
                        // ✅ NOUVEAU : Vérifier avant chaque téléchargement
                        if (window._undoInProgress) {
                            throw new Error('Undo in progress');
                        }

                        await downloadAndCacheAudio(segment);
                        loadedCount++;
                        setCacheProgress(Math.round((loadedCount / speakerSegments.length) * 100));
                    } catch (error) {
                        if (error.message === 'Undo in progress') {
                            console.log(`[${speakerId}] Stopped preload due to undo`);
                            return;
                        }
                        if (error.message.includes('File not found after undo') ||
                            (error.response && error.response.status === 404)) {
                            skippedCount++;
                            console.log(`[${speakerId}] Skipped missing file: ${segment.segment_file}`);
                        } else {
                            console.warn(`[${speakerId}] Failed to preload ${segment.segment_file}:`, error);
                        }
                    }
                })
            );
        }

        // ✅ NOUVEAU : Vérifier une dernière fois avant de marquer comme prêt
        if (!window._undoInProgress) {
            setIsCacheReady(true);
            console.log(`[${speakerId}] Preload complete: ${loadedCount}/${speakerSegments.length} (${skippedCount} skipped)`);
        }
    }, [transcript, downloadAndCacheAudio, speakerId]);



    // Effet pour détecter les changements dans le transcript
    useEffect(() => {
        if (!transcript || transcript.length === 0) return;

        const speakerType = speakerId.startsWith('BACKGROUND') ? 'BACKGROUND' :
            speakerId.startsWith('SILENT') ? 'SILENT' : 'SPEAKER';
        const fullSpeakerId = `${speakerType}_${speakerId.split('_')[1]}`;

        // Récupérer les segments de ce speaker
        const currentSpeakerSegments = transcript.filter(t =>
            t.speaker === fullSpeakerId && t.segment_file
        );

        // Vérifier si des segments ont changé (basé sur le hash des propriétés)
        currentSpeakerSegments.forEach(segment => {
            const newCacheKey = generateCacheKey(projectId, segment);
            const existingEntry = findCacheEntryForSegment(projectId, segment.segment_file);

            if (existingEntry && existingEntry.key !== newCacheKey) {
                // Le segment a changé, invalider l'ancienne version
                console.log(`[${speakerId}] Segment modified, invalidating cache: ${segment.segment_file}`);
                URL.revokeObjectURL(existingEntry.value);
                audioCache.delete(existingEntry.key);

                // Si c'est le segment actuel, le recharger
                if (currentSegmentRef.current?.segment_file === segment.segment_file) {
                    loadAndPlaySegment(segment, currentTime);
                }
            }
        });
    }, [transcript, speakerId, projectId, currentTime, loadAndPlaySegment]);

    // Effet pour précharger au montage
    useEffect(() => {
        if (!isCacheReady && transcript.length > 0) {
            preloadAllAudios();
        }
    }, [preloadAllAudios, isCacheReady, transcript]);

    // EFFET PRINCIPAL - Gérer les changements de segment
    // EFFET PRINCIPAL - Gérer les changements de segment
    useEffect(() => {
        const segment = findSegmentAtTime(currentTime);

        // Vérifier si on doit changer de segment
        if (segment?.segment_file !== currentSegmentRef.current?.segment_file) {
            // Débounce pour éviter les appels multiples
            const timeoutId = setTimeout(() => {
                loadAndPlaySegment(segment, currentTime);
            }, 50);

            return () => clearTimeout(timeoutId);
        } else if (segment && audioRef.current && !audioRef.current.seeking) {
            // Ajuster le temps si nécessaire
            const relativeTime = currentTime - segment.start;
            if (Math.abs(audioRef.current.currentTime - relativeTime) > 0.1) {
                audioRef.current.currentTime = relativeTime;
            }
        }
    }, [currentTime, findSegmentAtTime, loadAndPlaySegment]);

    // EFFET CRITIQUE - Gérer le MUTE
    useEffect(() => {
        if (!audioRef.current) return;

        // Appliquer le mute immédiatement
        audioRef.current.muted = muted;

        // Ajuster aussi le volume pour être sûr
        if (currentSegmentRef.current) {
            const gain = currentSegmentRef.current.audio_gain || 0.8;
            audioRef.current.volume = muted ? 0 : gain;
        }

        console.log(`[${speakerId}] MUTE UPDATED: muted=${muted}, volume=${audioRef.current.volume}, audioMuted=${audioRef.current.muted}`);
    }, [muted, speakerId]);

    // Effet pour gérer play/pause
    useEffect(() => {
        const handlePlayPause = async () => {
            if (!currentSegmentRef.current) return;

            if (!audioRef.current?.src) {
                console.log(`[${speakerId}] No source, skipping play/pause`);
                return;
            }

            const shouldPlay = isPlaying &&
                currentTime >= currentSegmentRef.current.start &&
                currentTime < currentSegmentRef.current.end;

            if (shouldPlay) {
                await safePlay();
            } else {
                await safePause();
            }
        };

        handlePlayPause();
    }, [isPlaying, currentTime, safePlay, safePause]);

    // Interface impérative
    useImperativeHandle(ref, () => ({
        play: safePlay,
        pause: safePause,
        getCurrentSegment: () => currentSegmentRef.current,
        isCacheReady: () => isCacheReady,
        getCacheProgress: () => cacheProgress,

        updateVolume: (segment) => {
            if (!audioRef.current || !currentSegmentRef.current) return;

            // Vérifier si c'est le bon segment
            if (currentSegmentRef.current.segment_file === segment.segment_file &&
                currentSegmentRef.current.start === segment.start) {

                const gain = segment.audio_gain || 0.8;

                // Appliquer le nouveau volume immédiatement
                if (!mutedRef.current) {
                    audioRef.current.volume = gain;
                    console.log(`[${speakerId}] Volume updated to ${gain} for ${segment.segment_file}`);
                }

                // Mettre à jour la référence du segment actuel avec le nouveau gain
                currentSegmentRef.current = {
                    ...currentSegmentRef.current,
                    audio_gain: segment.audio_gain
                };
            }
        },

        // Dans la méthode resetCache (ligne ~480)
        resetCache: () => {
            console.log(`[${speakerId}] Resetting cache...`);

            const projectPrefix = `${projectId}_`;

            // Vider la source audio pour éviter les erreurs
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current.load();
            }

            // Nettoyer le cache pour ce projet
            const deletedKeys = [];
            for (const [key, blobUrl] of audioCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    URL.revokeObjectURL(blobUrl);
                    audioCache.delete(key);
                    deletedKeys.push(key);
                }
            }

            console.log(`[${speakerId}] Deleted ${deletedKeys.length} cache entries`);

            // Réinitialiser l'état
            setCurrentSegment(null);
            currentSegmentRef.current = null;
            setCacheProgress(0);
            setIsCacheReady(false);

            // ✅ NOUVEAU : Précharger seulement si pas d'undo en cours
            if (!window._undoInProgress) {
                setTimeout(() => {
                    preloadAllAudios();
                }, 500);
            }
        },

        invalidateSegment: (segmentFile) => {
            console.log(`[${speakerId}] Invalidating cache for: ${segmentFile}`);
            const projectPrefix = `${projectId}_${segmentFile}_`;
            const keysToDelete = [];
            for (const [key, blobUrl] of audioCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    URL.revokeObjectURL(blobUrl);
                    keysToDelete.push(key);
                }
            }
            keysToDelete.forEach(key => audioCache.delete(key));
            console.log(`[${speakerId}] Deleted ${keysToDelete.length} cache entries for ${segmentFile}`);
        },

        invalidateCache: (segmentFile) => {
            // Alias pour compatibilité
            return ref.current?.invalidateSegment(segmentFile);
        },

        reloadCurrentSegment: () => {
            if (currentSegmentRef.current) {
                console.log(`[${speakerId}] Force reloading current segment: ${currentSegmentRef.current.segment_file}`);
                // Invalider d'abord le cache
                const projectPrefix = `${projectId}_${currentSegmentRef.current.segment_file}_`;
                for (const [key, blobUrl] of audioCache.entries()) {
                    if (key.startsWith(projectPrefix)) {
                        URL.revokeObjectURL(blobUrl);
                        audioCache.delete(key);
                    }
                }
                // Puis recharger
                loadAndPlaySegment(currentSegmentRef.current, currentTime);
            }
        },

        // Nouvelle méthode pour forcer le rechargement d'un segment spécifique
        forceReloadSegment: async (updatedSegment) => {
            console.log(`[${speakerId}] Force reload requested for: ${updatedSegment.segment_file}`);

            // Invalider toutes les versions en cache de ce segment
            const projectPrefix = `${projectId}_${updatedSegment.segment_file}_`;
            for (const [key, blobUrl] of audioCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    URL.revokeObjectURL(blobUrl);
                    audioCache.delete(key);
                    console.log(`[${speakerId}] Removed cache: ${key}`);
                }
            }

            // Si c'est le segment actuel, le recharger immédiatement
            if (currentSegmentRef.current?.segment_file === updatedSegment.segment_file) {
                await loadAndPlaySegment(updatedSegment, currentTime);
            } else {
                // Sinon, précharger pour la prochaine fois
                await downloadAndCacheAudio(updatedSegment);
            }
        }
    }), [safePlay, safePause, isCacheReady, cacheProgress, projectId, preloadAllAudios, currentTime, loadAndPlaySegment, speakerId, downloadAndCacheAudio]);

    // Nettoyage au démontage
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    return <audio ref={audioRef} style={{ display: 'none' }} />;
});

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;